import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import { MAX_INVITE_EMAILS } from '@gitbook/app-database';
import {
    Description,
    FieldGroup,
    intlPluralizeString,
    Label,
    Switch,
    toast,
} from '@gitbook/spine-react';

import { apiClient, apiQuery } from '../../state';
import {
    BulkInviteLimitAlert,
    PERMISSION_ROLES_DESCRIPTORS,
    PermissionRoleButton,
} from '../Organization';
import type {
    EmailTag,
    PermissionTag,
    TeamTag,
    UserTag,
} from '../ShareContent/InviteMembers/state';
import { UserTagInput } from '../Users';
import { SITE_PERMISSION_ROLE_GROUPS } from './sitePermissionRoleGroups';

/**
 * Invite users and teams to a site: the fields to pick them and their role, and the action that
 * invites them. Laid out by the caller, like the site's invite dialog does.
 *
 * @param input.onInvited - Called once the invites are sent.
 */
export function useSiteInviteForm(input: {
    organizationId: string;
    site: api.Site;
    onInvited: () => void;
}): { fields: React.ReactNode; canInvite: boolean; invite: () => Promise<void> } {
    const { organizationId, site, onInvited } = input;

    const { data: organization } = useSuspenseQuery(
        apiQuery('getOrganizationById', { organizationId })
    );
    // Under enforced SSO, people join through the IdP, so only existing members can be invited.
    const canAddGuests = organization.permissions.admin && !organization.sso;

    // Writes the site permission. Site mode is ensured by the caller before any invite runs.
    const onInvite = React.useCallback(
        async (invite: { role: api.MemberRoleOrGuest; users: string[]; teams: string[] }) => {
            let invited = false;
            await toast.catch(async () => {
                await apiClient.sitePermissions.inviteToSite(organizationId, site.id, invite);
                invited = true;
            }, 'Error while inviting users');
            return invited;
        },
        [organizationId, site.id]
    );

    const [tags, setTags] = React.useState<PermissionTag[]>([]);
    const [permission, setPermission] = React.useState<api.MemberRoleOrGuest>(api.MemberRole.Edit);
    const [inviteAsOrgMember, setInviteAsOrgMember] = React.useState(true);

    const { emails, users, teams } = React.useMemo(() => {
        return {
            emails: tags.filter((tag): tag is EmailTag => tag.type === 'email'),
            users: tags.filter((tag): tag is UserTag => tag.type === 'user'),
            teams: tags.filter((tag): tag is TeamTag => tag.type === 'team'),
        };
    }, [tags]);
    const organizationInviteRole =
        permission === null ? null : PERMISSION_ROLES_DESCRIPTORS[permission];
    const inviteEmailsAsOrgMember = inviteAsOrgMember && organizationInviteRole !== null;

    const invite = React.useCallback(async () => {
        if (emails.length > MAX_INVITE_EMAILS) {
            toast.error('Error while updating the permissions', null, {
                description: `You can only invite up to ${MAX_INVITE_EMAILS} emails at once.`,
            });
            return;
        }

        let usersToAdd = users.map((u) => u.user);
        let guestsCount = 0;

        if (emails.length > 0) {
            await toast.catch(async () => {
                const { data } = await apiClient.organizationInvites.inviteUsersToOrganization(
                    organizationId,
                    {
                        emails: emails.map((emailTag) => emailTag.email),
                        role: inviteEmailsAsOrgMember ? permission : null,
                    }
                );

                usersToAdd = usersToAdd.concat(...data.users);
                if (!inviteEmailsAsOrgMember) {
                    guestsCount = data.invited;
                }
            }, 'Error while inviting users');
        }

        const invited = await onInvite({
            role: permission,
            users: usersToAdd,
            teams: teams.map((team) => team.team),
        });

        if (!invited) {
            return;
        }

        const usersCount = usersToAdd.length - guestsCount;

        toast.success(
            `${intlPluralizeString({
                count: usersCount,
                singular: 'Invite',
                plural: 'Invites',
            })} sent`
        );

        onInvited();
    }, [
        onInvited,
        emails,
        inviteEmailsAsOrgMember,
        onInvite,
        organizationId,
        permission,
        teams,
        users,
    ]);

    const fields = (
        <div className="flex flex-col gap-6">
            <UserTagInput
                organization={organizationId}
                canInviteUsers={canAddGuests}
                value={tags}
                onChangeValue={setTags}
                button={
                    <PermissionRoleButton
                        size="small"
                        kind="blank"
                        roleGroups={SITE_PERMISSION_ROLE_GROUPS}
                        value={permission}
                        onValueChange={setPermission}
                        icon={null}
                        // No "Deny access" when inviting — denying happens from the members
                        // list. Inviting someone with no access proved to be confusing for
                        // lots of users.
                        allowNullValue={false}
                    />
                }
            />

            <BulkInviteLimitAlert emailCount={emails.length} />

            {canAddGuests && emails.length > 0 ? (
                <div className="rounded-sm border border-card bg-secondary px-4 py-2">
                    <FieldGroup orientation="horizontal">
                        <FieldGroup orientation="vertical">
                            <Label>Add email invitees to the organization</Label>
                            <Description className="font-normal text-muted">
                                {organizationInviteRole ? (
                                    inviteEmailsAsOrgMember ? (
                                        <>
                                            New email addresses will join the organization as{' '}
                                            <strong>{organizationInviteRole.title}</strong> and
                                            receive the same role on this site.
                                        </>
                                    ) : (
                                        <>
                                            New email addresses will join the organization as{' '}
                                            <strong>Guests</strong> and only receive the{' '}
                                            <strong>{organizationInviteRole.title}</strong> role on
                                            this site.
                                        </>
                                    )
                                ) : (
                                    'Choose a site role to add new email addresses to the organization.'
                                )}
                            </Description>
                        </FieldGroup>
                        <Switch
                            aria-label="Add email invitees to the organization"
                            value={inviteEmailsAsOrgMember}
                            disabled={organizationInviteRole === null}
                            onValueChange={setInviteAsOrgMember}
                        />
                    </FieldGroup>
                </div>
            ) : null}
        </div>
    );

    return { fields, canInvite: tags.length > 0, invite };
}
